import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import Dropdown, {
  DropdownEventEnum,
} from "@booking/components/compounds/forms/Dropdown";
import Fieldset from "@booking/components/containers/forms/Fieldset";
import { ButtonActionEnum, IconEnum, SizeEnum } from "@booking/enums";
import { useSubscribe } from "@booking/hooks/usePubSub";
import { DEPARTURE_AIRPORTS } from "@sitewide/constants/enquiryData";
import React, { useState } from "react";

import DatePicker from "@/components/compounds/forminputs/DatePicker";
import { ColorEnum } from "@/enums";
import type { PubSubData } from "@/types";

import {
  CALL_TO_ACTION_TEXT,
  PHONE_LINK,
  PHONE_NUMBER_DISPLAY,
} from "../utils/constants";
import { getTomorrowDate } from "../utils/dateUtils";
import { createNumberOptions, createUpdateControl } from "../utils/formHelpers";
import styles from "./CreateYourOwnForm.module.css";

interface CreateYourOwnTravelFormProps {
  onContinue: (travelDetails: CreateYourOwnTravelFormData) => void;
  initialData?: Partial<CreateYourOwnTravelFormData>;
  courseNames?: string[];
  isEu?: boolean;
}

export interface CreateYourOwnTravelFormData {
  arrival_date: string;
  group_size: string;
  nights: string;
  rounds: string;
  courses?: Record<string, number> | null;
  include_flight_options?: boolean;
  flight_options?: string;
}

const initialFormData: CreateYourOwnTravelFormData = {
  arrival_date: getTomorrowDate(),
  group_size: "1",
  nights: "1",
  rounds: "1",
  include_flight_options: false,
};

const groupSizeOptions = createNumberOptions(10);
const nightsOptions = createNumberOptions(10);
const roundsOptions = createNumberOptions(10);
const departureAirportOptions = DEPARTURE_AIRPORTS.map((option) => ({
  text: option.text,
  value: option.value,
}));

// Input field names
const INPUTS = {
  ARRIVAL_DATE: "arrival_date",
  GROUP_SIZE: "group_size",
  NIGHTS: "nights",
  ROUNDS: "rounds",
  FLIGHT_OPTIONS: "flight_options",
};

function CreateYourOwnTravelForm(props: CreateYourOwnTravelFormProps) {
  const {
    onContinue,
    initialData = {},
    courseNames = [],
    isEu = false,
  } = props;

  // Initialize course rounds state - default to 1 for each course
  const initialCourseRounds: Record<string, number> = {};
  courseNames.forEach((courseName) => {
    initialCourseRounds[courseName] = initialData.courses?.[courseName] || 1;
  });

  const [courseRounds, setCourseRounds] =
    useState<Record<string, number>>(initialCourseRounds);

  const [formData, setFormData] = useState<CreateYourOwnTravelFormData>({
    ...initialFormData,
    ...initialData,
  });

  const updateForm = (data: Partial<CreateYourOwnTravelFormData>) => {
    // Clear flight_options when checkbox is unchecked
    if (data.include_flight_options === false) {
      setFormData((prev) => ({
        ...prev,
        ...data,
        flight_options: "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, ...data }));
    }
  };

  const updateControl =
    createUpdateControl<CreateYourOwnTravelFormData>(updateForm);

  // Calculate total rounds from course selections
  const calculateTotalRounds = (rounds: Record<string, number>): number => {
    return Object.values(rounds).reduce((sum, value) => sum + value, 0);
  };

  // Handle course rounds change
  const handleCourseRoundsChange = (courseName: string, value: string) => {
    const newRounds = { ...courseRounds, [courseName]: parseInt(value, 10) };
    setCourseRounds(newRounds);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // When there are courses, calculate total rounds from course selections
    // When there are no courses, use the rounds field from formData
    const totalRounds =
      courseNames.length > 0
        ? calculateTotalRounds(courseRounds)
        : parseInt(formData.rounds, 10);

    // Build courses object - include all courses with their selected values (including 0)
    // If no courses, set to null instead of empty object
    const coursesData: Record<string, number> | null =
      courseNames.length > 0 ? { ...courseRounds } : null;

    // Update formData with calculated rounds and courses
    const submitData: CreateYourOwnTravelFormData = {
      ...formData,
      rounds: totalRounds.toString(),
      courses: coursesData,
    };

    onContinue(submitData);
  };

  // Subscribe to form events
  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [
    INPUTS.GROUP_SIZE,
    INPUTS.NIGHTS,
    INPUTS.ROUNDS, // Subscribe to rounds dropdown (for venues with no courses)
    INPUTS.FLIGHT_OPTIONS, // Subscribe to departure airport dropdown
  ]);

  // Subscribe to course rounds changes
  const handleCourseDropdownChange = (
    data: PubSubData & { context: string },
  ) => {
    if (data.context.startsWith("course_")) {
      const courseName = data.context.replace("course_", "");
      handleCourseRoundsChange(courseName, data.value);
    }
  };

  useSubscribe(
    DropdownEventEnum.CHANGE,
    handleCourseDropdownChange,
    courseNames.map((name) => `course_${name}`),
  );

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formInner}>
        <DatePicker
          id={INPUTS.ARRIVAL_DATE}
          name={INPUTS.ARRIVAL_DATE}
          value={formData.arrival_date}
          onChange={(e) =>
            updateForm({ [INPUTS.ARRIVAL_DATE]: e.target.value })
          }
          min={getTomorrowDate()}
          label="Arriving On"
          labelInside
        />

        <Dropdown
          name={INPUTS.GROUP_SIZE}
          options={groupSizeOptions}
          value={formData.group_size}
          label="Group size"
          icon={IconEnum.USER}
          labelInside
        />

        <Dropdown
          name={INPUTS.NIGHTS}
          options={nightsOptions}
          value={formData.nights}
          label="Number of nights"
          icon={IconEnum.MOON}
          labelInside
        />

        {courseNames.length > 0 ? (
          <Fieldset
            legend="Number of rounds"
            noBorder
            noPadding
            legendBackground={false}
          >
            <div className={styles.coursesContainer}>
              {courseNames.map((courseName) => (
                <Dropdown
                  key={courseName}
                  name={`course_${courseName}`}
                  options={roundsOptions}
                  value={courseRounds[courseName]?.toString() || "0"}
                  label={courseName}
                  icon={IconEnum.GOLF_BALL_TEE}
                  labelInside
                />
              ))}
            </div>
          </Fieldset>
        ) : (
          <Dropdown
            name={INPUTS.ROUNDS}
            options={roundsOptions}
            value={formData.rounds}
            label="Number of rounds"
            icon={IconEnum.GOLF_BALL_TEE}
            labelInside
          />
        )}

        {isEu && (
          <div className={styles.checkboxContainer}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="include_flight_options"
                checked={formData.include_flight_options || false}
                onChange={(e) =>
                  updateForm({ include_flight_options: e.target.checked })
                }
                className={styles.checkbox}
              />
              <span>Yes, I'm interested in flight options</span>
            </label>
          </div>
        )}

        {isEu && formData.include_flight_options && (
          <Dropdown
            name={INPUTS.FLIGHT_OPTIONS}
            options={departureAirportOptions}
            value={formData.flight_options || ""}
            label="Departure airport (optional)"
            icon={IconEnum.CHECK}
            labelInside
          />
        )}
      </div>

      <Button
        color={ColorEnum.DARKGREEN}
        size={SizeEnum.LARGE}
        type={ButtonActionEnum.SUBMIT}
        variant={ButtonVariantEnum.SOLID}
      >
        CREATE MY ENQUIRY
      </Button>

      <div className={styles.callInfo}>
        <p>
          {CALL_TO_ACTION_TEXT} <a href={PHONE_LINK}>{PHONE_NUMBER_DISPLAY}</a>{" "}
          from 8:30AM today
        </p>
      </div>
    </form>
  );
}

export default CreateYourOwnTravelForm;
export { CreateYourOwnTravelForm, type CreateYourOwnTravelFormProps };
